import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  PlaylistAdd as PlaylistAddIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getVirusScans, createWhitelist, createBlacklist } from '../../services/api/securityApi';
import { VirusScanResult } from '../../types/analysis';

const VirusScanListPage: React.FC = () => {
  const [scans, setScans] = useState<VirusScanResult[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filters
  const [riskLevel, setRiskLevel] = useState('');
  const [search, setSearch] = useState('');

  // Detail Dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedScan, setSelectedScan] = useState<VirusScanResult | null>(null);

  // Add to List Dialog
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [listType, setListType] = useState<'whitelist' | 'blacklist'>('whitelist');
  const [listFormData, setListFormData] = useState({
    appName: '',
    developer: '',
    category: '',
    description: '',
    threatType: '',
  });

  useEffect(() => {
    fetchScans();
  }, [page, rowsPerPage, riskLevel, search]);

  const fetchScans = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getVirusScans({
        page: page + 1,
        limit: rowsPerPage,
        riskLevel: (riskLevel as 'SAFE' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL') || undefined,
        search: search || undefined,
      });
      setScans(data.items);
      setTotal(data.total);
    } catch (err: any) {
      setError(err.message || '데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetail = (scan: VirusScanResult) => {
    setSelectedScan(scan);
    setDetailOpen(true);
  };

  const handleOpenListDialog = (scan: VirusScanResult, type: 'whitelist' | 'blacklist') => {
    setSelectedScan(scan);
    setListType(type);
    setListFormData({
      appName: scan.fileName || '',
      developer: '',
      category: type === 'whitelist' ? 'OFFICIAL' : '',
      description: '',
      threatType: type === 'blacklist' ? 'MALWARE' : '',
    });
    setListDialogOpen(true);
  };

  const handleAddToList = async () => {
    if (!selectedScan) return;

    try {
      const packageName = selectedScan.fileName || selectedScan.sha256;
      
      if (listType === 'whitelist') {
        await createWhitelist({
          packageName,
          appName: listFormData.appName,
          developer: listFormData.developer,
          category: listFormData.category,
          description: listFormData.description,
          source: 'ADMIN',
        });
        setSuccess('화이트리스트에 추가되었습니다.');
      } else {
        // Convert risk level to lowercase for blacklist
        const riskLevelMap: Record<string, 'low' | 'medium' | 'high' | 'critical'> = {
          'SAFE': 'low',
          'LOW': 'low',
          'MEDIUM': 'medium',
          'HIGH': 'high',
          'CRITICAL': 'critical',
        };
        const mappedRiskLevel = riskLevelMap[selectedScan.riskLevel || 'MEDIUM'] || 'medium';
        
        await createBlacklist({
          packageName,
          sha256: selectedScan.sha256,
          appName: listFormData.appName,
          threatType: listFormData.threatType,
          riskLevel: mappedRiskLevel,
          description: listFormData.description,
          source: 'ADMIN',
        });
        setSuccess('블랙리스트에 추가되었습니다.');
      }
      
      setListDialogOpen(false);
      setListFormData({
        appName: '',
        developer: '',
        category: '',
        description: '',
        threatType: '',
      });
    } catch (err: any) {
      setError(err.message || '추가에 실패했습니다.');
    }
  };

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'CRITICAL':
        return 'error';
      case 'HIGH':
        return 'error';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
        return 'info';
      default:
        return 'default';
    }
  };

  const getRiskLevelLabel = (level: string) => {
    switch (level) {
      case 'CRITICAL':
        return '치명적';
      case 'HIGH':
        return '높음';
      case 'MEDIUM':
        return '중간';
      case 'LOW':
        return '낮음';
      default:
        return '알 수 없음';
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">악성 앱 스캔</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchScans}
          disabled={loading}
        >
          새로고침
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="SHA256 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            select
            label="위험도"
            value={riskLevel}
            onChange={(e) => setRiskLevel(e.target.value)}
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">전체</MenuItem>
            <MenuItem value="CRITICAL">치명적</MenuItem>
            <MenuItem value="HIGH">높음</MenuItem>
            <MenuItem value="MEDIUM">중간</MenuItem>
            <MenuItem value="LOW">낮음</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>SHA256</TableCell>
              <TableCell>파일명</TableCell>
              <TableCell>위험도</TableCell>
              <TableCell>위험 요소</TableCell>
              <TableCell>스캔 날짜</TableCell>
              <TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : scans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  데이터가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              scans.map((scan) => (
                <TableRow key={scan._id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {scan.sha256.substring(0, 16)}...
                    </Typography>
                  </TableCell>
                  <TableCell>{scan.fileName || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={getRiskLevelLabel(scan.riskLevel || 'UNKNOWN')}
                      color={getRiskLevelColor(scan.riskLevel || 'UNKNOWN') as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      탐지: {scan.scanResult?.positives || 0} / {scan.scanResult?.total || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {new Date(scan.createdAt).toLocaleString('ko-KR')}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="상세보기">
                      <IconButton size="small" onClick={() => handleViewDetail(scan)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="화이트리스트 추가">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleOpenListDialog(scan, 'whitelist')}
                      >
                        <PlaylistAddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="블랙리스트 추가">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenListDialog(scan, 'blacklist')}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="페이지당 행 수:"
        />
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>스캔 상세 정보</DialogTitle>
        <DialogContent>
          {selectedScan && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                SHA256
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', mb: 2, wordBreak: 'break-all' }}
              >
                {selectedScan.sha256}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                파일명
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedScan.fileName || 'N/A'}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                위험도 평가
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={getRiskLevelLabel(selectedScan.riskLevel || 'UNKNOWN')}
                  color={getRiskLevelColor(selectedScan.riskLevel || 'UNKNOWN') as any}
                  sx={{ mr: 1 }}
                />
                <Typography variant="body2" component="span">
                  탐지: {selectedScan.scanResult?.positives || 0} / {selectedScan.scanResult?.total || 0}
                </Typography>
              </Box>

              {selectedScan.detectedThreats && selectedScan.detectedThreats.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    탐지된 위협
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {selectedScan.detectedThreats.map((threat, index) => (
                      <Chip
                        key={index}
                        label={threat}
                        size="small"
                        color="error"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                  </Box>
                </>
              )}

              {selectedScan.scanResult?.permalink && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    VirusTotal 링크
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <a href={selectedScan.scanResult.permalink} target="_blank" rel="noopener noreferrer">
                      {selectedScan.scanResult.permalink}
                    </a>
                  </Typography>
                </>
              )}

              <Typography variant="subtitle2" gutterBottom>
                스캔 날짜
              </Typography>
              <Typography variant="body2">
                {new Date(selectedScan.createdAt).toLocaleString('ko-KR')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* Add to List Dialog */}
      <Dialog open={listDialogOpen} onClose={() => setListDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {listType === 'whitelist' ? '화이트리스트 추가' : '블랙리스트 추가'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="앱 이름"
              value={listFormData.appName}
              onChange={(e) => setListFormData({ ...listFormData, appName: e.target.value })}
              fullWidth
              required
            />

            {listType === 'whitelist' ? (
              <>
                <TextField
                  label="개발자"
                  value={listFormData.developer}
                  onChange={(e) => setListFormData({ ...listFormData, developer: e.target.value })}
                  fullWidth
                />
                <TextField
                  select
                  label="카테고리"
                  value={listFormData.category}
                  onChange={(e) => setListFormData({ ...listFormData, category: e.target.value })}
                  fullWidth
                  required
                >
                  <MenuItem value="OFFICIAL">공식 앱</MenuItem>
                  <MenuItem value="BANKING">금융</MenuItem>
                  <MenuItem value="GOVERNMENT">정부</MenuItem>
                  <MenuItem value="UTILITY">유틸리티</MenuItem>
                  <MenuItem value="OTHER">기타</MenuItem>
                </TextField>
              </>
            ) : (
              <TextField
                select
                label="위협 유형"
                value={listFormData.threatType}
                onChange={(e) => setListFormData({ ...listFormData, threatType: e.target.value })}
                fullWidth
                required
              >
                <MenuItem value="MALWARE">악성코드</MenuItem>
                <MenuItem value="SPYWARE">스파이웨어</MenuItem>
                <MenuItem value="ADWARE">애드웨어</MenuItem>
                <MenuItem value="TROJAN">트로이목마</MenuItem>
                <MenuItem value="RANSOMWARE">랜섬웨어</MenuItem>
                <MenuItem value="PHISHING">피싱</MenuItem>
                <MenuItem value="OTHER">기타</MenuItem>
              </TextField>
            )}

            <TextField
              label="설명"
              value={listFormData.description}
              onChange={(e) => setListFormData({ ...listFormData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setListDialogOpen(false)}>취소</Button>
          <Button onClick={handleAddToList} variant="contained" color="primary">
            추가
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VirusScanListPage;
